import { useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";

export interface UseTierReturn {
  tier: string;
  isPremium: boolean;
  isFree: boolean;
  canAccess: (requiredTier: string) => boolean;
  mealLimit: number | null;
  mealsUsed: number;
  totalMeals: number;
  activePlanLimit: number | null;
  activePlansUsed: number;
  collectionLimit: number | null;
  collectionsUsed: number;
  hasReachedMealLimit: boolean;
  hasReachedActivePlanLimit: boolean;
  hasReachedCollectionLimit: boolean;
  isNearMealLimit: boolean;
  isNearActivePlanLimit: boolean;
  isNearCollectionLimit: boolean;
}

/**
 * Hook that exposes the current user's account tier, resource limits,
 * and derived helpers.
 *
 * Limits are parameterised on the backend so they can be changed at will.
 */
export function useTier(): UseTierReturn {
  const { user, limits } = useAuth();

  const tier = user?.tier ?? "free";
  const isPremium = user !== null && tier === "premium";
  const isFree = user !== null && tier === "free";

  const canAccess = useMemo(
    () =>
      (requiredTier: string): boolean => {
        if (requiredTier === "free") return true;
        if (requiredTier === "premium") return isPremium;
        return tier === requiredTier;
      },
    [isPremium, tier],
  );

  const mealLimit = limits?.meals ?? null;
  const mealsUsed = limits?.meals_used ?? 0;
  const totalMeals = limits?.total_meals ?? 0;
  const activePlanLimit = limits?.active_meal_plans ?? null;
  const activePlansUsed = limits?.active_meal_plans_used ?? 0;

  const collectionLimit = limits?.collections ?? null;
  const collectionsUsed = limits?.collections_used ?? 0;

  const hasReachedMealLimit = mealLimit !== null && mealsUsed >= mealLimit;
  const hasReachedActivePlanLimit =
    activePlanLimit !== null && activePlansUsed >= activePlanLimit;
  const hasReachedCollectionLimit =
    collectionLimit !== null && collectionsUsed >= collectionLimit;
  const isNearMealLimit =
    mealLimit !== null && mealsUsed >= mealLimit - 2 && mealsUsed < mealLimit;
  const isNearActivePlanLimit =
    activePlanLimit !== null &&
    activePlansUsed >= activePlanLimit - 1 &&
    activePlansUsed < activePlanLimit;
  const isNearCollectionLimit =
    collectionLimit !== null &&
    collectionsUsed >= collectionLimit - 1 &&
    collectionsUsed < collectionLimit;

  return {
    tier,
    isPremium,
    isFree,
    canAccess,
    mealLimit,
    mealsUsed,
    totalMeals,
    activePlanLimit,
    activePlansUsed,
    collectionLimit,
    collectionsUsed,
    hasReachedMealLimit,
    hasReachedActivePlanLimit,
    hasReachedCollectionLimit,
    isNearMealLimit,
    isNearActivePlanLimit,
    isNearCollectionLimit,
  };
}
